/**
 * @module components/Table/TableHazard
 * @author Charles Blais <charles.blais@canada.ca>
 *
 * Hazard table that takes an array of site desgination objects
 */

import React, { useMemo } from 'react';

import { useTable, useFilters, FilterProps } from 'react-table';

import { Table, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { CSVLink  } from 'react-csv';

import { useLanguageState, LanguageState } from '../../contexts/language-state-provider';

import { flattenSiteDesignations } from '../../lib';


type Props = {
  siteDesignations: SiteDesignationsXVResponse | SiteDesignationsXSResponse,
  meta: HazardParameters,
  defaultFiltered?: {
    id: string,
    value: string,
  }[],
}

type PropsReactTable = {
  columns: any[],
  data: SiteDesignation[],
  defaultFiltered: {
    id: string,
    value: string,
  }[],
  meta: HazardParameters,
}


const DefaultColumnFilter = <T extends Record<string, unknown>>({
  column
}: FilterProps<T>) => {
  // @ts-ignore: believe there is a problem in ColumnInstance type
  const { preFilteredRows, filterValue, setFilter } = column;
  const [ lang ] = useLanguageState();

  const count = preFilteredRows.length;
  return (
    <FormControl
      value={filterValue || ''}
      onChange={e => {
        // Set undefined to remove the filter entirely
        setFilter((e.target as HTMLButtonElement).value || undefined)
      }}
      style={{width:'100%'}}
      placeholder={`${lang.table.search} ${count} ${lang.table.records}...`}
    />
  )
};


const SelectColumnFilter = <T extends Record<string, unknown>>({
  column
}: FilterProps<T>) => {
  // @ts-ignore: believe there is a problem in ColumnInstance type
  const { preFilteredRows, filterValue, setFilter, id } = column;
  const [ lang ] = useLanguageState();

  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options = new Set<string>()
    preFilteredRows.forEach((row: any) => {
      options.add(row.values[id])
    })
    return [...options]
  }, [id, preFilteredRows])

  return (
    <FormControl
      componentClass='select'
      value={filterValue}
      onChange={e => {
        setFilter((e.target as HTMLButtonElement).value || undefined)
      }}
    >
      <option value="">{lang.table.all}</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </FormControl>
  )
};

/**
 * Convert the site designations list into a list
 * that is react-table friendly.
 * 
 * Site designation is an object where the key is the siteDesignation
 * value.
 * 
 * @param {*} siteDesignations 
 */
const makeData = (
  siteDesignations: SiteDesignationsXVResponse | SiteDesignationsXSResponse
): SiteDesignation[] => {
  return flattenSiteDesignations(siteDesignations);
}


/**
 * Use the sa units to generate to create the column def
 * @param {*} saUnits
 * @param {*} lang
 */
const makeColumns = (lang: LanguageState) => {
  return [{
    Header: lang.table.siteDesignation,
    accessor: 'siteDesignation',
    Filter: SelectColumnFilter,
    filter: 'equals',
  }, {
    Header: lang.table.poe50,
    accessor: 'poe50',
    Filter: SelectColumnFilter,
    filter: 'equals',
  }, {
    Header: 'Sa(0.05)',
    accessor: 'sa0p05',
    disableFilters: true,
  }, {
    Header: 'Sa(0.1)',
    accessor: 'sa0p1',
    disableFilters: true,
  }, {
    Header: 'Sa(0.2)',
    accessor: 'sa0p2',
    disableFilters: true,
  }, {
    Header: 'Sa(0.3)',
    accessor: 'sa0p3',
    disableFilters: true,
  }, {
    Header: 'Sa(0.5)',
    accessor: 'sa0p5',
    disableFilters: true,
  }, {
    Header: 'Sa(1.0)',
    accessor: 'sa1p0',
    disableFilters: true,
  }, {
    Header: 'Sa(2.0)',
    accessor: 'sa2p0',
    disableFilters: true,
  }, {
    Header: 'Sa(5.0)',
    accessor: 'sa5p0',
    disableFilters: true,
  }, {
    Header: 'Sa(10.0)',
    accessor: 'sa10p0',
    disableFilters: true,
  }, {
    Header: 'PGA',
    accessor: 'pga',
    disableFilters: true,
  }, {
    Header: 'PGV',
    accessor: 'pgv',
    disableFilters: true,
  }];
}

/**
 * React component extention of the TableHazard
 * ============================================
 */
const TableReactModel = ({
  columns,
  data,
  defaultFiltered,
  meta,
}: PropsReactTable) => {
  const [ lang ] = useLanguageState();
  // Column hook
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }), []);

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    // @ts-ignore: TODO
    defaultColumn,
    // @ts-ignore: TODO
    initialState: { filters: defaultFiltered }
  }, useFilters);
  
  let dateTime = new Date()
  
  let CSVHeaders = [
    { label: "Code", key: "Code"},
    { label: "Latitude", key: "Latitude"},
    { label: "Longitude", key: "Longitude"},
    { label: "Site Designation", key: "Site Designation"},
    { label: "Probability (% in 50 years)", key: "Probability"},
    { label: "Sa(0.05) [g]", key: "Sa(0.05)"},
    { label: "Sa(0.1) [g]", key: "Sa(0.1)"},
    { label: "Sa(0.2) [g]", key: "Sa(0.2)"},
    { label: "Sa(0.3) [g]", key: "Sa(0.3)"},
    { label: "Sa(0.5) [g]", key: "Sa(0.5)"},
    { label: "Sa(1.0) [g]", key: "Sa(1.0)"},
    { label: "Sa(2.0) [g]", key: "Sa(2.0)"},
    { label: "Sa(5.0) [g]", key: "Sa(5.0)"},
    { label: "Sa(10.0) [g]", key: "Sa(10.0)"},
    { label: "PGA [g]", key: "PGA"},
    { label: "PGV [m/s]", key: "PGV"},
    { label: "Date Generated", key: "Date Generated"},
  ];

  if (lang.abbr === 'fr') {
    CSVHeaders = [
      { label: "Code", key: "Code"},
      { label: "Latitude", key: "Latitude"},
      { label: "Longitude", key: "Longitude"},
      { label: "Désignation d'emplacement", key: "Désignation d'emplacement"},
      { label: "Probabilité (% en 50 ans)", key: "Probabilité"},
      { label: "Sa(0.05) [g]", key: "Sa(0.05)"},
      { label: "Sa(0.1) [g]", key: "Sa(0.1)"},
      { label: "Sa(0.2) [g]", key: "Sa(0.2)"},
      { label: "Sa(0.3) [g]", key: "Sa(0.3)"},
      { label: "Sa(0.5) [g]", key: "Sa(0.5)"},
      { label: "Sa(1.0) [g]", key: "Sa(1.0)"},
      { label: "Sa(2.0) [g]", key: "Sa(2.0)"},
      { label: "Sa(5.0) [g]", key: "Sa(5.0)"},
      { label: "Sa(10.0) [g]", key: "Sa(10.0)"},
      { label: "PGA [g]", key: "PGA"},
      { label: "PGV [m/s]", key: "PGV"},
      { label: "Date de création", key: "Date Generated"},
    ];
  }

  const filteredData = rows.map((row) => {
    return Object.values(row.values).reduce((acc, cur, i) => {
      // @ts-ignore: we know the Header is defined everywhere
     
      acc[headerGroups[0].headers[i].Header] = cur;

      acc['Date Generated'] = dateTime
      acc['Code'] = meta.code
      acc['Latitude'] = meta.latitude
      acc['Longitude'] = meta.longitude

      return acc;
    }, {});
  });

  const headerFormatter = (header: any) => {
    if(header.startsWith('Sa')) return <>S<sub>a</sub>{header.substring(2)}</>
    else return header;
  }

  let filename = `${meta.latitude}_${meta.longitude}_${meta.code}_additionalValues.csv`

  if (lang.abbr === 'fr') {
    filename = `${meta.latitude}_${meta.longitude}_${meta.code}_valeursAdditionnelles.csv`
  }

  // Render the UI for your table
  return (
    <div>
      <Table bordered striped hover condensed {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <th {...column.getHeaderProps()}>
                  {
                  // @ts-ignore: canFilter does exist so ignore typing error
                  column.canFilter ? (
                    <FormGroup controlId={String(idx)}>
                      <ControlLabel>{headerFormatter(column.render('Header'))}</ControlLabel>
                      {column.render('Filter')}
                    </FormGroup>
                  ) : headerFormatter(column.render('Header'))
                  }
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>
                      {(cell.column.id === 'siteDesignation')
                        ? <>{cell.value.charAt(0)}<sub>{cell.value.substring(1)}</sub></>
                        : cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      <CSVLink
        data={filteredData}
        headers={CSVHeaders}
        filename={filename}
        className="btn btn-primary"
        target="_blank"
      >
        {lang.table.download}
      </CSVLink>
    </div>
  )
};


/**
 * React component
 * ===============
 */
const TableModel = ({
  siteDesignations,
  meta,
  defaultFiltered = [],
}: Props) => {
  const [ lang ] = useLanguageState();
  const columns = useMemo(() => makeColumns(lang), [lang]);
  const data = useMemo(() => makeData(siteDesignations), [siteDesignations]);

  return (
    <TableReactModel
      columns={columns}
      data={data}
      defaultFiltered={defaultFiltered}
      meta={meta}
    />
  )
};

export default TableModel;